import type { FC, PointerEvent, ReactNode } from 'react'

import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { Wordmark } from '@/components/generals/wordmark'
import { testimonials } from '@/constants/home'

interface DragState {
    pointerId: number
    startX: number
    startScroll: number
    moved: boolean
}

export const TestimonialsSlider: FC = (): ReactNode => {
    const scrollerRef = useRef<HTMLDivElement>(null)
    const drag = useRef<DragState | null>(null)
    const [atStart, setAtStart] = useState(true)
    const [atEnd, setAtEnd] = useState(false)
    const [dragging, setDragging] = useState(false)

    const updateEdges = useCallback(() => {
        const scroller = scrollerRef.current

        if (!scroller) return

        setAtStart(scroller.scrollLeft <= 4)
        setAtEnd(scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 4)
    }, [])

    useEffect(() => {
        const scroller = scrollerRef.current

        if (!scroller) return

        updateEdges()
        scroller.addEventListener('scroll', updateEdges, { passive: true })
        window.addEventListener('resize', updateEdges)

        return () => {
            scroller.removeEventListener('scroll', updateEdges)
            window.removeEventListener('resize', updateEdges)
        }
    }, [updateEdges])

    const step = () => {
        const scroller = scrollerRef.current
        const card = scroller?.querySelector<HTMLElement>('[data-slide]')

        if (!scroller || !card) return 0

        const gap = parseFloat(getComputedStyle(scroller).columnGap || '0') || 0

        return card.offsetWidth + gap
    }

    const go = (direction: 1 | -1) => {
        scrollerRef.current?.scrollBy({ left: direction * step(), behavior: 'smooth' })
    }

    // Mouse dragging. Touch keeps the browser's native swipe with snapping.
    const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
        const scroller = scrollerRef.current

        if (!scroller || event.pointerType !== 'mouse' || event.button !== 0) return

        drag.current = { pointerId: event.pointerId, startX: event.clientX, startScroll: scroller.scrollLeft, moved: false }
    }

    const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
        const scroller = scrollerRef.current
        const state = drag.current

        if (!scroller || !state || state.pointerId !== event.pointerId) return

        const delta = event.clientX - state.startX

        if (!state.moved && Math.abs(delta) > 4) {
            state.moved = true
            setDragging(true)
            scroller.setPointerCapture(event.pointerId)
            scroller.style.scrollSnapType = 'none'
            scroller.style.scrollBehavior = 'auto'
        }

        if (state.moved) scroller.scrollLeft = state.startScroll - delta
    }

    const endDrag = (event: PointerEvent<HTMLDivElement>) => {
        const scroller = scrollerRef.current
        const state = drag.current

        drag.current = null

        if (!scroller || !state || !state.moved) return

        if (scroller.hasPointerCapture(event.pointerId)) scroller.releasePointerCapture(event.pointerId)

        const size = step()
        const index = size ? Math.round(scroller.scrollLeft / size) : 0

        scroller.style.scrollBehavior = ''
        scroller.scrollTo({ left: index * size, behavior: 'smooth' })

        window.setTimeout(() => {
            scroller.style.scrollSnapType = ''
            setDragging(false)
        }, 450)
    }

    return (
        <section id='testimonials' aria-labelledby='testimonials-title' className='overflow-hidden py-24 md:py-32'>
            <Reveal className='fc-container flex flex-col gap-8 md:flex-row md:items-end md:justify-between'>
                <h2 id='testimonials-title' className='max-w-[760px] text-[clamp(2.25rem,4.6vw,4rem)] font-medium leading-[1.04] tracking-[-0.04em]'>
                    <span className='text-ink'>Leaders trust us.</span>{' '}
                    <span className='text-silver'>Operators depend on us.</span>
                </h2>

                <div className='flex shrink-0 items-center gap-3'>
                    <button
                        type='button'
                        onClick={() => go(-1)}
                        disabled={atStart}
                        aria-label='Previous testimonial'
                        aria-controls='testimonials-track'
                        className='inline-flex size-12 items-center justify-center rounded-full bg-white text-ink ring-1 ring-inset ring-[#e3e3e9] transition-colors hover:bg-mist disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'
                    >
                        <ArrowLeft className='size-5' aria-hidden='true' />
                    </button>

                    <button
                        type='button'
                        onClick={() => go(1)}
                        disabled={atEnd}
                        aria-label='Next testimonial'
                        aria-controls='testimonials-track'
                        className='inline-flex size-12 items-center justify-center rounded-full bg-ink text-white transition-colors hover:bg-[#26262b] disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'
                    >
                        <ArrowRight className='size-5' aria-hidden='true' />
                    </button>
                </div>
            </Reveal>

            <Reveal delay={120} className='mt-12 md:mt-16'>
                <div
                    ref={scrollerRef}
                    id='testimonials-track'
                    role='region'
                    aria-roledescription='carousel'
                    aria-label='Customer testimonials'
                    tabIndex={0}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onClickCapture={event => {
                        if (dragging) event.preventDefault()
                    }}
                    className={cn(
                        'no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-[var(--fc-gutter)] [scroll-padding-inline:var(--fc-gutter)] focus-visible:outline-none md:gap-6',
                        dragging ? 'cursor-grabbing select-none' : 'cursor-grab'
                    )}
                >
                    {testimonials.map((item, index) => (
                        <article
                            key={item.company}
                            data-slide
                            aria-roledescription='slide'
                            aria-label={`${index + 1} of ${testimonials.length}`}
                            className='relative isolate flex h-[500px] w-[86vw] max-w-[700px] shrink-0 snap-start flex-col justify-between overflow-hidden rounded-[24px] bg-night p-7 text-white sm:h-[540px] sm:w-[600px] sm:p-10 lg:w-[680px]'
                        >
                            <Image
                                src={item.image}
                                alt={item.imageAlt}
                                fill
                                draggable={false}
                                sizes='(min-width: 1024px) 680px, (min-width: 640px) 600px, 86vw'
                                className='-z-20 object-cover'
                            />

                            <div
                                aria-hidden='true'
                                className='absolute inset-0 -z-10'
                                style={{ background: `linear-gradient(180deg, rgba(14,14,14,0.38) 0%, rgba(14,14,14,0.18) 32%, ${item.tint} 78%)` }}
                            />

                            <Wordmark name={item.company} className='text-[22px] text-white' />

                            <div className='flex flex-col gap-8'>
                                <blockquote className='text-[clamp(1.375rem,2.3vw,1.875rem)] font-normal leading-[1.28] tracking-[-0.02em] text-white'>
                                    &ldquo;{item.quote}&rdquo;
                                </blockquote>

                                <div className='flex items-center gap-3'>
                                    <Image
                                        src={item.avatar}
                                        alt=''
                                        width={40}
                                        height={40}
                                        draggable={false}
                                        className='size-10 shrink-0 rounded-full object-cover object-top ring-2 ring-white/20'
                                    />

                                    <p className='flex flex-wrap items-center gap-x-2 text-[14px] font-medium'>
                                        <span>{item.name}</span>
                                        <span aria-hidden='true' className='text-white/50'>/</span>
                                        <span className='text-white/75'>{item.title}</span>
                                    </p>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </Reveal>
        </section>
    )
}
